/* eslint-disable react/no-unstable-nested-components */
import React, { useState } from 'react';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Label } from './ui/label';
import { Share2, Copy, CheckCircle, MessageCircle, Mail, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

/**
 * Referral share button. Mirrors the dashboard overview `ShareSavingsButton`
 * UX: on devices that expose the Web Share API (all modern mobile OSes,
 * many recent desktop browsers), one tap opens the native OS share sheet
 * with WhatsApp / Messenger / Mail / etc. pre-populated. Only when the
 * platform doesn't support native share (older desktop) do we fall back
 * to the in-app social-picker dialog.
 */
export function ReferralShareButton({ referralCode, userName }) {
  const [copied, setCopied] = useState(false);
  const [busy, setBusy] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);

  const referralLink = `${window.location.origin}/register?ref=${referralCode}`;
  const shareText = `Join All Global Access using my referral code: ${referralCode}. Get unlimited access, exclusive discounts, and earn rewards! Sign up here:`;
  const nativeShareText = `${shareText}\n${referralLink}`;

  const copy = async (text, label = 'Link copied!') => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      toast.success(label);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error('Could not copy — long-press to copy manually');
    }
  };

  const onPrimaryShare = async () => {
    if (busy) return;
    setBusy(true);
    try {
      if (typeof navigator !== 'undefined' && typeof navigator.share === 'function') {
        try {
          await navigator.share({
            title: 'All Global Access',
            text: shareText,
            url: referralLink,
          });
          toast.success('Thanks for sharing!');
        } catch (err) {
          // User cancelled — do nothing. Anything else, open the fallback.
          if (err?.name !== 'AbortError') {
            setDialogOpen(true);
          }
        }
      } else {
        // No Web Share API → open in-app picker
        setDialogOpen(true);
      }
    } finally {
      setBusy(false);
    }
  };

  const shareOptions = [
    {
      name: 'Facebook',
      icon: () => <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>,
      color: '#1877F2',
      onClick: () => window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(referralLink)}&quote=${encodeURIComponent(shareText)}`, '_blank'),
    },
    {
      name: 'Twitter / X',
      icon: () => <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>,
      color: '#000000',
      onClick: () => window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(nativeShareText)}`, '_blank'),
    },
    {
      name: 'WhatsApp',
      icon: MessageCircle,
      color: '#25D366',
      onClick: () => window.open(`https://wa.me/?text=${encodeURIComponent(nativeShareText)}`, '_blank'),
    },
    {
      name: 'Telegram',
      icon: () => <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.479.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"/></svg>,
      color: '#0088cc',
      onClick: () => window.open(`https://t.me/share/url?url=${encodeURIComponent(referralLink)}&text=${encodeURIComponent(shareText)}`, '_blank'),
    },
    {
      name: 'Email',
      icon: Mail,
      color: '#EA4335',
      onClick: () => window.open(`mailto:?subject=${encodeURIComponent('Join All Global Access!')}&body=${encodeURIComponent(nativeShareText)}`, '_blank'),
    },
  ];

  return (
    <>
      <Button
        onClick={onPrimaryShare}
        disabled={busy}
        className="bg-[#007AFF]/20 text-[#007AFF] hover:bg-[#007AFF]/30 border border-[#007AFF]/30"
        data-testid="share-referral-btn"
      >
        {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Share2 className="w-4 h-4 mr-2" />}
        Share
      </Button>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-[#041A3B] border-white/10 text-white max-w-sm">
          <DialogHeader>
            <DialogTitle>Share Your Referral Link</DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <p className="text-[#A0B4CC] text-sm">Earn commissions when people sign up using your link!</p>

            <div className="p-3 rounded-lg bg-[#000B18] border border-white/10">
              <Label className="text-[#5C7599] text-xs">Your referral link</Label>
              <div className="flex items-center gap-2 mt-1">
                <code className="flex-1 text-[#00E5FF] text-xs font-mono break-all">{referralLink}</code>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => copy(referralLink, 'Link copied!')}
                  className={copied ? 'text-[#00E676]' : 'text-[#A0B4CC]'}
                  data-testid="copy-referral-link"
                >
                  {copied ? <CheckCircle className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                </Button>
              </div>
            </div>

            <div className="flex items-center justify-between p-3 rounded-lg bg-[#000B18] border border-white/10">
              <div>
                <span className="text-[#5C7599] text-xs">Referral Code</span>
                <p className="text-white font-mono font-bold text-lg">{referralCode}</p>
              </div>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => copy(referralCode, 'Code copied!')}
                className="text-[#A0B4CC]"
              >
                <Copy className="w-4 h-4" />
              </Button>
            </div>

            <div className="grid grid-cols-5 gap-2">
              {shareOptions.map((opt) => {
                const IconComponent = opt.icon;
                return (
                  <button
                    key={opt.name}
                    onClick={opt.onClick}
                    className="flex flex-col items-center gap-1.5 p-3 rounded-lg hover:bg-white/5 transition-colors group"
                    data-testid={`share-${opt.name.toLowerCase().replace(/\s/g, '-')}`}
                  >
                    <div
                      className="w-10 h-10 rounded-full flex items-center justify-center transition-colors"
                      style={{ backgroundColor: `${opt.color}20`, color: opt.color }}
                    >
                      <IconComponent />
                    </div>
                    <span className="text-[#5C7599] text-[10px] group-hover:text-[#A0B4CC]">{opt.name}</span>
                  </button>
                );
              })}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
